import clsx from 'clsx';
import * as React from 'react';

import type * as api from '@gitbook/api-client';
import { DateShortRelative } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    AtomLink,
    Description,
    Heading,
    renderIcon,
    Skeleton,
    TextSkeleton,
} from '@gitbook/spine-react';
import { getChangeRequestSubject } from '@gitbook/util-format';

import { NavigationLink } from '../../navigation';
import { useSpace } from '../Space';
import { UserProfilePrettyName } from '../Users';
import {
    CHANGE_REQUEST_ICON_COLOR_CLASSNAMES,
    CHANGE_REQUEST_STATUS_ICONS,
} from './ChangeRequestIcon';
import { ChangeRequestMoreButton } from './ChangeRequestMoreButton';

/**
 * A card representing a change request in a list.
 */
export function ChangeRequestListCard(props: {
    organizationId: string;
    changeRequest: api.ChangeRequest;

    /**
     * If provided, the card will be active and will have a ring.
     */
    active?: boolean;

    /**
     * If false, the card will be displayed as in the context of a specific space.
     */
    withSpace?: boolean;
}): React.ReactElement {
    const { organizationId, changeRequest, active, withSpace = true } = props;
    const space = useSpace({ organizationId, spaceId: changeRequest.space });

    return (
        <AtomLink
            href={{
                screen: withSpace
                    ? {
                          type: 'change_requests',
                          organization: organizationId,
                          selected: {
                              spaceId: changeRequest.space,
                              changeRequestNumber: changeRequest.number,
                          },
                      }
                    : {
                          type: 'space',
                          space: changeRequest.space,
                          organization: organizationId,
                          changeRequest: changeRequest.number,
                          page: '',
                          sideSheet: undefined,
                      },
            }}
            className={clsx(
                'flex cursor-pointer flex-col rounded-lg border border-solid text-start hover:bg-[var(--sp-color-comments-comment-bg-hover)] hover:shadow-subtle',
                active ? 'border-[var(--sp-color-border-comment-active)] ring-2' : 'border-base'
            )}
        >
            <div className="flex flex-col gap-2 p-3">
                <div className="flex flex-row justify-between gap-2">
                    <div className="flex flex-row gap-2">
                        {renderIcon(CHANGE_REQUEST_STATUS_ICONS[changeRequest.status], {
                            className: clsx(
                                'mt-0.5 size-4',
                                CHANGE_REQUEST_ICON_COLOR_CLASSNAMES[changeRequest.status]
                            ),
                        })}
                        <div className="flex-1 text-base text-ui-action">
                            {getChangeRequestSubject(changeRequest)}
                        </div>
                    </div>
                    {space ? (
                        <ChangeRequestMoreButton
                            space={space}
                            changeRequest={changeRequest}
                            size="small"
                            kind="nested"
                        />
                    ) : null}
                </div>
                <div>
                    <p className="text-muted text-ui-small">
                        <React.Suspense fallback={<TextSkeleton className="w-1/2" />}>
                            <ChangeRequestTagline
                                organizationId={organizationId}
                                changeRequest={changeRequest}
                                space={withSpace && space ? space : undefined}
                            />
                        </React.Suspense>
                    </p>
                </div>
            </div>
        </AtomLink>
    );
}

export function ChangeRequestTagline(props: {
    organizationId: string;
    changeRequest: api.ChangeRequest;
    space?: api.Space;
}): React.ReactElement {
    const { organizationId, changeRequest, space } = props;
    return (
        <>
            #{changeRequest.number} opened{' '}
            <DateShortRelative value={changeRequest.createdAt} onlyDate={false} /> by{' '}
            <UserProfilePrettyName user={changeRequest.createdBy} />
            {space ? (
                <>
                    {' '}
                    in{' '}
                    <NavigationLink
                        kind="inherit"
                        to={
                            space
                                ? {
                                      screen: {
                                          type: 'space',
                                          space: space.id,
                                          organization: organizationId,
                                          changeRequest: changeRequest.number,
                                          page: '',
                                          sideSheet: undefined,
                                      },
                                  }
                                : undefined
                        }
                    >
                        {space?.title ?? 'Deleted space'}
                    </NavigationLink>
                </>
            ) : null}
        </>
    );
}

export function ChangeRequestListCardSkeleton(): React.ReactElement {
    return (
        <div className="flex flex-col gap-2 rounded-lg border border-solid p-3 text-start">
            <div className="flex flex-row items-center gap-3">
                <Skeleton shape="circle" className="size-10">
                    <Icon.ChangeRequestDraft />
                </Skeleton>
                <Heading className="flex-1">
                    <TextSkeleton className={'w-1/2'} />
                </Heading>
            </div>
            <Description>
                <TextSkeleton className={'w-full'} />
            </Description>
        </div>
    );
}
